import {AlertTriangle, Trash2} from 'lucide-react'
import React, {FC, ReactNode} from 'react'
import {createPortal} from 'react-dom'

interface Props {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  isDark: boolean
  /** `danger` = red confirm (deletes); `primary` = blue confirm (apply / discard). */
  tone?: 'danger' | 'primary'
  /** Extra content under the body (e.g. an input), above the buttons. */
  children?: ReactNode
  /** Greys out the confirm button (e.g. while the input above is invalid). */
  confirmDisabled?: boolean
}

/**
 * Small confirmation dialog styled to the onboarding sheets (the shared MUI ConfirmationModal
 * carries hardcoded English and a different design language). Portals above the sheet's
 * z-index; backdrop click or Cancel dismisses.
 */
const WizardConfirmDialog: FC<Props> = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isDark,
  tone = 'danger',
  children,
  confirmDisabled = false,
}) => {
  if (!open) return null
  const accent = tone === 'danger' ? '#f1416c' : '#056EE9'
  const accentBg = tone === 'danger' ? 'rgba(241,65,108,0.12)' : 'rgba(5,110,233,0.12)'
  const accentShadow = tone === 'danger' ? 'rgba(241,65,108,0.35)' : 'rgba(5,110,233,0.35)'
  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1080,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onCancel}
        style={{position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)'}}
      />
      <div
        style={{
          position: 'relative',
          width: 'min(380px, calc(100vw - 2rem))',
          borderRadius: 14,
          border: `1px solid ${isDark ? '#3f4254' : '#e4e6ef'}`,
          background: isDark ? '#1a1b23' : '#ffffff',
          padding: '1.4rem',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        }}
      >
        <div className='d-flex align-items-center gap-2 mb-2'>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 10,
              background: accentBg,
              color: accent,
              flexShrink: 0,
            }}
          >
            {tone === 'danger' ? <Trash2 size={16} /> : <AlertTriangle size={16} />}
          </span>
          <span style={{color: isDark ? '#ffffff' : '#181C32', fontWeight: 700, fontSize: '1rem'}}>
            {title}
          </span>
        </div>
        <p
          style={{
            color: isDark ? '#92929f' : '#7E8299',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            marginBottom: '1.1rem',
          }}
        >
          {body}
        </p>
        {children}
        <div className='d-flex justify-content-end gap-2'>
          <button
            type='button'
            className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={{
              opacity: confirmDisabled ? 0.5 : 1,
              cursor: confirmDisabled ? 'not-allowed' : 'pointer',
              border: 'none',
              borderRadius: 10,
              padding: '0.45rem 1.1rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#ffffff',
              background: accent,
              boxShadow: `0 6px 16px ${accentShadow}`,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default WizardConfirmDialog
